/**
 * Resolution of a field's **default value** (#388).
 *
 * Deliberately a plain, dependency-free module (no `@Injectable`, no Angular imports): it is pure logic, it is
 * exercised by plain Jasmine specs without a `TestBed`, and it is imported by both the form factory (which
 * builds every control) and its tests.
 */

import {
  ARRAY_VALUE_FIELD_TYPES,
  DefaultToken,
  defaultTokensFor,
  FieldBlock,
  FieldOption,
  FieldType,
  OPTION_FIELD_TYPES,
  supportsDefaultValue
} from '../models/form-schema.model';

/**
 * The value a control of this type starts with when it carries no usable default — i.e. exactly the pre-#388
 * behaviour. Kept as its own function so "no default" and "default was rejected" provably land on the same
 * value and nothing regresses for the overwhelming majority of fields that declare no default at all.
 */
export function emptyValueFor(type: FieldType | string | undefined): any {
  if (type === 'boolean') return false;
  if (ARRAY_VALUE_FIELD_TYPES.includes(type as FieldType)) return [];
  return '';
}

/**
 * The value a **new** form control should start with for `field`.
 *
 * Precedence, highest first:
 * 1. {@link FieldBlock.defaultToken} — but only when the token is listed in {@link defaultTokensFor} for this
 *    field's type (`today` on a `date`, `now` on a `time`). A token that is not valid for the type is ignored
 *    and resolution falls through to the literal.
 * 2. {@link FieldBlock.defaultValue} — coerced to the type's own value shape, and dropped when it cannot be
 *    made to fit (a non-numeric literal on a `number`, a malformed date, an option that no longer exists).
 * 3. {@link emptyValueFor} — the type's empty value, which is what every field used before #388.
 *
 * Types in `DEFAULT_VALUE_UNSUPPORTED_TYPES` (`file` / `signature`) ignore any declared default entirely: there
 * is no literal an author could pre-set for a picked file or a drawn signature.
 *
 * **Defaults are new-submission-only, by design.** They are applied here, at control-construction time, and the
 * runtime only ever fills a control from a default where the incoming submission data carries *no key at all*
 * for it. A stored value — including a stored empty one the employee deliberately left blank — always wins;
 * otherwise re-opening a submission would silently rewrite real answers, which is a data-integrity problem in a
 * form that can carry payroll- and identity-relevant fields.
 *
 * @param field the field block being built into a control
 * @param now the instant dynamic tokens resolve against; injectable so specs never assert against the real
 *            clock. Resolved with **local** date/time components (never `toISOString()`, which would shift the
 *            day for anyone east/west of UTC) — the employee's own local day is what they expect to see.
 * @returns the control's starting value
 */
export function resolveDefault(field: FieldBlock, now: Date = new Date()): any {
  const empty = emptyValueFor(field?.type);
  if (!field || !supportsDefaultValue(field.type)) return empty;

  // 1. A valid dynamic token beats a literal, so an author who set both gets the "live" one.
  if (field.defaultToken && defaultTokensFor(field.type).includes(field.defaultToken)) {
    return resolveToken(field.defaultToken, now);
  }

  // 2./3. Literal, or the type's empty value when the literal is unusable.
  const literal = coerceLiteral(field, field.defaultValue);
  return literal === undefined ? empty : literal;
}

// --- internals ---------------------------------------------------------------

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

/** Resolves a dynamic token against `now`, using local components (see {@link resolveDefault}). */
function resolveToken(token: DefaultToken, now: Date): string {
  switch (token) {
    case 'today':
      return `${pad(now.getFullYear(), 4)}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    case 'now':
      return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  }
}

/**
 * Coerces a literal `defaultValue` into the field type's value shape.
 *
 * @returns the coerced value, or `undefined` when the literal is unusable and must be ignored.
 */
function coerceLiteral(field: FieldBlock, raw: any): any {
  const type = field.type;

  // Array-valued types first: they accept a single scalar and wrap it, so `null` is the only "nothing".
  if (ARRAY_VALUE_FIELD_TYPES.includes(type)) {
    if (raw === null || raw === undefined) return undefined;
    const members = Array.isArray(raw) ? raw : [raw];
    // Only the members that still match a live option survive — deleting an option must not leave a phantom
    // selection behind. Non-matching members are dropped individually; the matching ones are kept.
    return members
      .map(member => matchOption(field.options, member))
      .filter((option): option is FieldOption => !!option)
      .map(option => option.value);
  }

  if (raw === null || raw === undefined) return undefined;

  switch (type) {
    case 'boolean': {
      if (typeof raw === 'boolean') return raw;
      if (typeof raw === 'string') {
        const text = raw.trim().toLowerCase();
        if (text === 'true') return true;
        if (text === 'false') return false;
      }
      return undefined;
    }
    case 'number': {
      const parsed = typeof raw === 'number'
        ? raw
        : (typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : Number.NaN);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    case 'date': {
      if (typeof raw !== 'string') return undefined;
      const text = raw.trim();
      return isCalendarDate(text) ? text : undefined;
    }
    case 'time': {
      if (typeof raw !== 'string') return undefined;
      return toClockTime(raw.trim());
    }
  }

  // Everything else is text-ish (including the option-based scalars `select` / `radio`).
  if (typeof raw === 'object') return undefined; // no meaningful string form — "[object Object]" helps nobody
  const text = String(raw);

  if (OPTION_FIELD_TYPES.includes(type)) {
    const option = matchOption(field.options, text);
    return option ? option.value : undefined;
  }

  return text;
}

/**
 * Finds the option a default value refers to.
 *
 * Matching is **loose on purpose**: {@link FieldOption.value} is typed `any` and authors routinely mix a numeric
 * option value with a string default (or the other way round — a JSON round-trip is enough to change which one
 * you get). A strict `===` would silently drop a default that visibly names a real option, which reads as a bug,
 * so both sides are compared stringified. The *option's* own value is then returned, so the control ends up
 * holding the canonical option value and the renderer's `===` / `includes` comparisons keep working.
 */
function matchOption(options: FieldOption[] | undefined, candidate: any): FieldOption | undefined {
  if (candidate === null || candidate === undefined) return undefined;
  return (options ?? []).find(option => String(option.value) === String(candidate));
}

/** `yyyy-MM-dd`, and a real calendar day (so `2025-02-30` is rejected). Validated in UTC — no local shift. */
function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}

/** `HH:mm`, accepting (and truncating) an `HH:mm:ss` literal. Returns `undefined` when it is not a clock time. */
function toClockTime(value: string): string | undefined {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours <= 23 && minutes <= 59 ? `${match[1]}:${match[2]}` : undefined;
}
